import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineGrid,
  DataTable,
  Text,
  Badge,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import api from '@/services/api';

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">{label}</Text>
        <Text as="p" variant="headingLg">{value}</Text>
      </BlockStack>
    </Card>
  );
}

export default function DailyManifest() {
  const navigate = useNavigate();

  const { data: manifest, isLoading } = useQuery({
    queryKey: ['daily-manifest'],
    queryFn: () => api.get('/shipping/manifest/today').then(r => r.data),
  });

  const rows = (manifest?.shipments || []).map((s: any) => [
    s.orderNumber || '\u2014',
    s.customerName || '\u2014',
    s.clinicName || '\u2014',
    s.productName || '\u2014',
    s.trackingNumber || '\u2014',
    <Badge key={s.id}>{s.carrier || 'N/A'}</Badge>,
    s.destination || '\u2014',
    s.cost ? `$${Number(s.cost).toFixed(2)}` : '\u2014',
    new Date(s.createdAt).toLocaleTimeString(),
  ]);

  const totalCost = (manifest?.shipments || []).reduce(
    (sum: number, s: any) => sum + (s.cost || 0),
    0,
  );

  return (
    <Page
      title="Daily Shipping Manifest"
      subtitle={manifest?.date || new Date().toISOString().split('T')[0]}
      backAction={{ content: 'Back', onAction: () => navigate(-1) }}
      primaryAction={{
        content: 'Print Manifest',
        onAction: () => window.print(),
      }}
    >
      <BlockStack gap="400">
        {isLoading ? (
          <Card><SkeletonBodyText lines={8} /></Card>
        ) : !manifest || manifest.shipments.length === 0 ? (
          <Card>
            <EmptyState
              heading="No shipments today"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>No shipping labels have been generated today.</p>
            </EmptyState>
          </Card>
        ) : (
          <>
            <InlineGrid columns={3} gap="400">
              <StatCard label="Total Shipments" value={String(manifest.totalShipments)} />
              <StatCard label="Total Shipping Cost" value={`$${totalCost.toFixed(2)}`} />
              <StatCard label="Date" value={manifest.date} />
            </InlineGrid>
            <Card>
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Order', 'Customer', 'Clinic', 'Product', 'Tracking #', 'Carrier', 'Destination', 'Cost', 'Time']}
                rows={rows}
              />
            </Card>
          </>
        )}
      </BlockStack>
    </Page>
  );
}
